import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle,
  ArrowLeft, Download, Loader2, Users, FileText, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientImportPage = () => {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      handlePreview(droppedFile);
    } else {
      toast({
        title: 'Format invalide',
        description: 'Veuillez utiliser un fichier CSV',
        variant: 'destructive'
      });
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      handlePreview(selectedFile);
    }
  };

  const handlePreview = async (selectedFile) => {
    setLoading(true);
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await fetch(`${API_URL}/api/clients/import/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        const error = await response.json();
        toast({
          title: 'Erreur de lecture',
          description: error.detail || 'Impossible de lire le fichier',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de l\'analyse du fichier',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    
    setImporting(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/clients/import/execute?skip_duplicates=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setPreview(null);
        
        toast({
          title: 'Import terminé',
          description: `${data.imported} clients importés avec succès`
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Erreur d\'import',
          description: error.detail || 'L\'import a échoué',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de l\'import',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients/import/download-template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'modele_import_clients.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de télécharger le modèle',
        variant: 'destructive'
      });
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="space-y-6" data-testid="client-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture/clients">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Upload className="h-6 w-6 text-blue-600" />
              Importer des clients
            </h1>
            <p className="text-slate-500 text-sm">Importez vos clients depuis un fichier CSV</p>
          </div>
        </div>
        <Button 
          variant="outline"
          onClick={handleDownloadTemplate}
          data-testid="download-template-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Télécharger le modèle
        </Button>
      </div>

      {/* Result */}
      {result && (
        <Card className={result.imported > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              {result.imported > 0 ? (
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              ) : (
                <AlertCircle className="h-8 w-8 text-amber-600" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">
                  {result.imported > 0 ? 'Import terminé !' : 'Import terminé avec des avertissements'}
                </h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-2xl font-bold text-emerald-600">{result.imported}</p>
                    <p className="text-sm text-slate-500">Importés</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                    <p className="text-sm text-slate-500">Doublons ignorés</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-2xl font-bold text-red-600">{result.total_errors || 0}</p>
                    <p className="text-sm text-slate-500">Erreurs</p>
                  </div>
                </div>
                
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Erreurs détectées:</p>
                    <div className="max-h-32 overflow-y-auto">
                      {result.errors.slice(0, 5).map((err, idx) => (
                        <p key={idx} className="text-sm text-red-600">
                          Ligne {err.row}: {err.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-4 flex gap-3">
                  <Link to="/dashboard/smartfacture/clients">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      Voir mes clients
                    </Button>
                  </Link>
                  <Button variant="outline" onClick={reset}>
                    Nouvel import
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Zone */}
      {!result && (
        <Card>
          <CardContent className="pt-6">
            <div 
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                dragOver 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              {loading ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
                  <p className="text-slate-600">Analyse du fichier...</p>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className={`h-12 w-12 mx-auto mb-4 ${
                    dragOver ? 'text-blue-600' : 'text-slate-400'
                  }`} />
                  <h3 className="text-lg font-medium text-slate-700 mb-2">
                    {file ? file.name : 'Glissez votre fichier CSV ici'}
                  </h3>
                  <p className="text-slate-500 mb-4">
                    ou cliquez pour sélectionner un fichier
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csv-upload"
                    data-testid="csv-upload"
                  />
                  <label htmlFor="csv-upload">
                    <Button variant="outline" className="cursor-pointer" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Sélectionner un fichier
                      </span>
                    </Button>
                  </label>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {preview && !result && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Aperçu de l'import
            </CardTitle>
            <CardDescription>
              Vérifiez les données avant d'importer
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-700">{preview.total_rows}</p>
                <p className="text-sm text-slate-500">Lignes totales</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-600">{preview.valid_rows}</p>
                <p className="text-sm text-slate-500">Valides</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{preview.invalid_rows}</p>
                <p className="text-sm text-slate-500">Invalides</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-lg font-medium text-blue-600">{preview.delimiter === ';' ? 'Point-virgule' : 'Virgule'}</p>
                <p className="text-sm text-slate-500">Séparateur</p>
              </div>
            </div>

            {/* Detected columns */}
            <div className="mb-6">
              <p className="text-sm font-medium text-slate-700 mb-2">Colonnes détectées:</p>
              <div className="flex flex-wrap gap-2">
                {preview.detected_columns?.map((col, idx) => (
                  <Badge key={idx} variant="outline" className="bg-slate-50">
                    {col}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Preview table */}
            <div className="border rounded-lg overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">#</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Nom</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Email</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Téléphone</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">SIREN</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {preview.previews?.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className={row.valid ? '' : 'bg-red-50'}>
                        <td className="px-4 py-2 text-sm text-slate-500">{row.row}</td>
                        <td className="px-4 py-2 text-sm font-medium text-slate-800">{row.name || '-'}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{row.email || '-'}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{row.phone || '-'}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{row.siren || '-'}</td>
                        <td className="px-4 py-2">
                          {row.valid ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <span className="text-xs text-red-600">{row.error}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.total_rows > 10 && (
                <div className="px-4 py-2 bg-slate-50 text-sm text-slate-500 text-center">
                  ... et {preview.total_rows - 10} autres lignes
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={reset}>
                Annuler
              </Button>
              <Button 
                onClick={handleImport}
                disabled={importing || preview.valid_rows === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="import-btn"
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Users className="h-4 w-4 mr-2" />
                )}
                Importer {preview.valid_rows} clients
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help */}
      {!preview && !result && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Comment préparer votre fichier ?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Téléchargez le modèle</p>
                  <p className="text-sm text-slate-500">Utilisez notre modèle CSV pour vous assurer que les colonnes sont correctes</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Remplissez vos données</p>
                  <p className="text-sm text-slate-500">Ajoutez vos clients avec au minimum le nom (obligatoire)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">Importez</p>
                  <p className="text-sm text-slate-500">Glissez le fichier ou cliquez pour le sélectionner</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientImportPage;
